/**
 * views/FiscalDocumentsView.js
 * ----------------------------------------------------------------------------
 * Every fiscal document the submitter has raised against URA EFRIS, in both
 * environments: commission invoices and the credit notes that correct them.
 * This is the screen URA watches during the User Acceptance Test, and the
 * screen ops opens when a vendor asks "did my receipt reach URA".
 *
 * WHY THE ENVIRONMENT IS PRINTED EVERYWHERE. URA's sandbox and production
 * issue numbers that look identical, and a sandbox FDN from this project has
 * already been read as a live one. So the page header carries the submitter's
 * environment, every row's fiscal document number carries the row's own mode
 * beside it, and the column never goes blank: a document without a number
 * says "Not issued" and still shows its mode. There is no way to copy a number
 * off this page without its environment coming with it.
 *
 * WHY THE APPLICATION REFERENCE IS NEVER IN THE NUMBER'S PLACE. A credit note
 * is an application URA approves by hand. Until it is approved it has a URA
 * application reference and NO fiscal document number, and the two must never
 * be confused (services/fiscal/fiscalGate.js exists to prevent exactly that).
 * The URA number cell therefore shows the fiscal document number, or "Not
 * issued", and the application reference only ever appears on its own line
 * underneath, labelled as an application reference, and again in the details.
 *
 * WHY SIX COLUMNS (31 Oct 2026). The table had eleven and scrolled sideways on
 * a laptop, which hid the action buttons and the dates (Izi: "fix the table
 * that requires horizontal scrolling"). Each column now carries one fact and
 * its one quieter line beneath: the document number with its kind and buyer;
 * the gross with its tax; the status with when it was filed or lodged; the
 * URA number with its verification code or application reference. Nothing
 * was dropped; the details row still prints every field in full. Every size
 * comes from TYPE, and only the document number is emphasised.
 *
 * RAISING A CREDIT NOTE (31 Oct 2026). Until now only a settled refund raised
 * one, so an invoice issued in error had no correction, and the credit note
 * scenario of URA's acceptance test could not be shown without refunding a
 * customer. An issued invoice's details now offer "Raise credit note" to a
 * tax:manage holder: the whole amount or part of it, with a reason that goes
 * to URA. The server writes the row (POST /api/fiscal-credit-notes) and the
 * submitter lodges it; this screen says plainly that nothing has reached URA
 * until the row shows URA's application reference.
 *
 * THE CREDIT NOTE LIFECYCLE, AS URA RUNS IT. A credit note is lodged as an
 * application (T110) and URA approves it by hand. The submitter asks URA about
 * each application on a schedule (T111, T112) and records the verdict, so an
 * awaiting row shows when it was last asked and when it will be asked next.
 * Once approved, the credit note is an issued document with its own FDN, and
 * only then can it be cancelled: URA cancels an issued credit note by its FDN
 * (T114), with a reason code from URA's refund reason list. So the cancel
 * action appears on an issued credit note and nowhere else, an application
 * says it is waiting on URA, and a cancellation shows REQUESTED until URA has
 * answered. URA's own words are printed apart from the submitter's, so the
 * reader is never told URA said something URA did not.
 *
 * THE DOCUMENT ITSELF. A row URA issued (an FDN and a verification code)
 * opens as the e-invoice or credit note the vendor receives, laid out as URA's
 * specification requires, in the same document dialog a customer receipt
 * prints from. The server decides what is printable and renders the page
 * (controllers/fiscalDocumentsController.js); this screen only offers the
 * button where the row says URA issued it, and shows the server's reason when
 * it refuses.
 *
 * @see controllers/fiscalCreditNoteController.js
 * @see components/fiscal/FiscalModeBadge.js
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Table } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  apiCancelFiscalCreditNote,
  apiGetFiscalDocumentLink,
  apiListFiscalDocuments,
  apiRaiseFiscalCreditNote,
} from "../api/index.js";
import { usePermissions } from "../utilities/usePermissions";
import { openReceiptDialog } from "../utilities/receiptDialogBus";
import { AppButton, AppChoice, AppDialog, AppSelect } from "../components/ui";
import Loading from "../components/Loading";
import TablePager from "../components/table/TablePager";
import FiscalModeBadge, { FiscalNumber } from "../components/fiscal/FiscalModeBadge.js";
import TaxHelp from "../components/fiscal/taxHelp.js";
import {
  DOCUMENT_STATUS,
  DeniedNotice,
  KIND_LABEL,
  PAGE,
  Pill,
  StatusPill,
  TYPE,
  errorText,
  fmtDateTime,
  fmtMoney,
  fmtVendorType,
} from "../components/fiscal/fiscalUi.js";

const PAGE_SIZE = 25;
const VENDOR_TYPES = ["RESTAURANT", "RETAILER", "WHOLESALER", "HEALTHCARE", "BRAND"];
const EMPTY_FILTERS = Object.freeze({ kind: "", status: "", mode: "", vendorType: "", from: "", to: "", q: "" });

/**
 * The filters a link may open this page on, read off the address once when the
 * page mounts: `/admin/fiscal-documents?status=REJECTED` is where the EFRIS
 * health page's "Show rejected documents" goes. Only the known filter names
 * are read, and only as text, so a link cannot set anything else.
 */
const filtersFromAddress = () => {
  const out = { ...EMPTY_FILTERS };
  try {
    const params = new URLSearchParams(window.location.search);
    Object.keys(EMPTY_FILTERS).forEach((key) => {
      const value = params.get(key);
      if (value) out[key] = String(value).trim();
    });
  } catch {
    /* No address to read (a test, a server render): the empty filters stand. */
  }
  return out;
};
const MIN_REASON = 3;

/** Query params the API gets: only the filters that are set. */
const toParams = (filters, page) => {
  const params = { page, limit: PAGE_SIZE };
  Object.entries(filters).forEach(([key, value]) => {
    const v = String(value || "").trim();
    if (v) params[key] = v;
  });
  return params;
};

/** A row that is a document URA issued, so it prints. The server decides again
    when asked (services/fiscal/fiscalDocumentView.js); this only hides the
    button on rows where the answer is already known to be no. */
const isPrintable = (row) =>
  Boolean(row.fdn && row.verificationCode) && (row.status === "SUBMITTED" || row.status === "WITHDRAWN");

const DOCUMENT_BUTTON = Object.freeze({ INVOICE: "e-Invoice", CREDIT_NOTE: "Credit note" });

const CANCELLATION_STATUS = Object.freeze({
  REQUESTED: { label: "Cancellation requested", tone: "warn" },
  CLAIMED: { label: "Cancellation with the submitter", tone: "warn" },
  REFUSED: { label: "Cancellation refused", tone: "bad" },
  CANCELLED: { label: "Cancelled at URA", tone: "neutral" },
});

/** URA's refund reason list (T114 reasonCode), in URA's words. */
const CANCELLATION_REASON_CODES = Object.freeze([
  { code: "101", label: "101 Buyer refused to accept the invoice due to incorrect invoice/receipt" },
  { code: "102", label: "102 Not delivered due to incorrect invoice/receipt" },
  { code: "103", label: "103 Other reasons" },
]);
const DEFAULT_REASON_CODE = "103";
const reasonCodeLabel = (code) => CANCELLATION_REASON_CODES.find((r) => r.code === code)?.label || code;

/** The table's column count, for the empty row and the details row. */
const COLUMNS = 6;

/** An issued invoice: the only document a credit note can answer. The server
    decides again (ALREADY_CREDITED, INVOICE_NOT_ISSUED); this only hides the
    action where the answer is already known to be no. */
const isCreditable = (row) => row.kind === "INVOICE" && row.status === "SUBMITTED" && Boolean(row.fdn);

/** An issued credit note: the only kind of document URA cancels. */
const isCancellable = (row) =>
  row.kind === "CREDIT_NOTE" && row.status === "SUBMITTED" && Boolean(row.fdn) &&
  (!row.cancellation || row.cancellation.status === "REFUSED");

/** Where the submitter has got to asking URA about a lodged application. */
const ApprovalCheckBlock = ({ row }) => {
  const check = row.approvalCheck;
  return (
    <div data-testid="approval-check" style={{ marginBottom: 18 }}>
      <div style={PAGE.sectionTitle}>URA approval<TaxHelp topic="applicationReference" /></div>
      <div style={PAGE.detailGrid}>
        <div style={PAGE.detailLabel}>Status</div>
        <div style={PAGE.detailValue}>
          Waiting on URA. The submitter asks URA about this application on a schedule; once approved it becomes an
          issued credit note with its own fiscal document number, and can then be cancelled if it has to be.
        </div>
        <div style={PAGE.detailLabel}>Last asked</div>
        <div style={PAGE.detailValue}>{check?.lastCheckedAt ? fmtDateTime(check.lastCheckedAt) : <span style={PAGE.muted}>Not asked yet</span>}</div>
        {check?.lastDetail ? (
          <>
            <div style={PAGE.detailLabel}>What came back</div>
            <div style={{ ...PAGE.detailValue, whiteSpace: "pre-wrap" }}>{check.lastDetail}</div>
          </>
        ) : null}
        {check?.nextCheckAt ? (
          <>
            <div style={PAGE.detailLabel}>Next check</div>
            <div style={PAGE.detailValue}>{fmtDateTime(check.nextCheckAt)}</div>
          </>
        ) : null}
      </div>
    </div>
  );
};

/** What an invoice offers: the credit note that corrects it. */
const CorrectionBlock = ({ row, canManage, onRaise }) => {
  const voided = row.documentStatus === "VOID";
  const why = voided
    ? "An invoice is corrected by a credit note, not cancelled. This one has been credited in full and is void."
    : isCreditable(row)
      ? "An invoice is corrected by a credit note, not cancelled. URA approves the credit note by hand."
      : "An invoice is corrected by a credit note, not cancelled. One can be raised once URA has issued this invoice.";
  return (
    <div>
      <div style={PAGE.sectionTitle}>Correction<TaxHelp topic="creditNote" /></div>
      <div style={{ ...TYPE.body, color: "#64748b", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <span data-testid="cancellation-none">{why}</span>
        {isCreditable(row) && !voided && canManage ? (
          <AppButton size="sm" icon="fa fa-reply" onClick={() => onRaise(row)} data-testid="raise-credit-note">
            Raise credit note
          </AppButton>
        ) : null}
      </div>
    </div>
  );
};

const CancellationBlock = ({ row, canManage, onRequest }) => {
  const cancellation = row.cancellation;
  const requestButton = isCancellable(row) && canManage ? (
    <AppButton size="sm" variant="danger" onClick={() => onRequest(row)} data-testid="cancel-credit-note">
      {cancellation ? "Request cancellation again" : "Request cancellation"}
    </AppButton>
  ) : null;
  if (!cancellation) {
    const why = row.status === "AWAITING_APPROVAL"
      ? "A credit note awaiting URA's approval cannot be cancelled. It can be once URA has issued it."
      : isCancellable(row)
        ? "No cancellation has been requested for this credit note."
        : "Only a credit note URA has issued can be cancelled.";
    return (
      <div>
        <div style={PAGE.sectionTitle}>Cancellation<TaxHelp topic="cancelCreditNote" /></div>
        <div style={{ ...TYPE.body, color: "#64748b", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <span data-testid="cancellation-none">{why}</span>
          {requestButton}
        </div>
      </div>
    );
  }
  const meta = CANCELLATION_STATUS[cancellation.status] || { label: String(cancellation.status || "Unknown"), tone: "neutral" };
  const pending = cancellation.status === "REQUESTED" || cancellation.status === "CLAIMED";
  return (
    <div data-testid="cancellation-block">
      <div style={PAGE.sectionTitle}>Cancellation<TaxHelp topic="cancelCreditNote" /></div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
        <Pill tone={meta.tone}>{meta.label}</Pill>
        {cancellation.requestedAt ? (
          <span style={{ ...TYPE.secondary, color: "#64748b" }}>Requested {fmtDateTime(cancellation.requestedAt)}</span>
        ) : null}
        {requestButton}
      </div>
      <div style={PAGE.detailGrid}>
        <div style={PAGE.detailLabel}>Reason code</div>
        <div style={PAGE.detailValue}>{cancellation.reasonCode ? reasonCodeLabel(cancellation.reasonCode) : <span style={PAGE.muted}>None recorded</span>}</div>
        <div style={PAGE.detailLabel}>Reason given</div>
        <div style={PAGE.detailValue}>{cancellation.reason || <span style={PAGE.muted}>None recorded</span>}</div>
        {pending ? (
          <>
            <div style={PAGE.detailLabel}>Answer</div>
            <div style={PAGE.detailValue}>Not yet. The credit note stands until URA confirms. Refresh the list to see the outcome.</div>
          </>
        ) : null}
        {cancellation.outcome ? (
          <>
            <div style={PAGE.detailLabel}>Outcome</div>
            <div style={{ ...PAGE.detailValue, whiteSpace: "pre-wrap" }} data-testid="cancellation-outcome">{cancellation.outcome}</div>
          </>
        ) : null}
        {!pending ? (
          <>
            <div style={PAGE.detailLabel}>URA&apos;s words</div>
            <div style={{ ...PAGE.detailValue, whiteSpace: "pre-wrap" }} data-testid="cancellation-ura-detail">
              {cancellation.uraDetail || <span style={PAGE.muted}>None. URA was not asked, so this answer is the submitter&apos;s.</span>}
            </div>
          </>
        ) : null}
        {cancellation.completedAt ? (
          <>
            <div style={PAGE.detailLabel}>Answered</div>
            <div style={PAGE.detailValue}>{fmtDateTime(cancellation.completedAt)}</div>
          </>
        ) : null}
      </div>
    </div>
  );
};

const DetailRow = ({ row, canManage, onRequestCancel, onRaiseCreditNote }) => (
  <tr className="lk-fiscal-detail">
    <td colSpan={COLUMNS} style={{ background: "#f8fafc", padding: "16px 20px 18px" }}>
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 24 }}>
        <div>
          <div style={PAGE.sectionTitle}>URA's view of this document</div>
          <div style={PAGE.detailGrid}>
            <div style={PAGE.detailLabel}>Fiscal document number</div>
            <div style={PAGE.detailValue}><FiscalNumber value={row.fdn} mode={row.mode} /></div>
            <div style={PAGE.detailLabel}>Verification code<TaxHelp topic="verificationCode" /></div>
            <div style={PAGE.detailValue}>{row.verificationCode ? <span style={PAGE.mono}>{row.verificationCode}</span> : <span style={PAGE.muted}>None</span>}</div>
            <div style={PAGE.detailLabel}>URA application reference<TaxHelp topic="applicationReference" /></div>
            <div style={PAGE.detailValue}>
              {row.applicationReference ? (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                  <span style={PAGE.mono}>{row.applicationReference}</span>
                  <span style={{ ...TYPE.caption, color: "#64748b" }}>not a fiscal document number</span>
                </span>
              ) : <span style={PAGE.muted}>None</span>}
            </div>
            {row.kind === "CREDIT_NOTE" ? (
              <>
                <div style={PAGE.detailLabel}>Revises</div>
                <div style={PAGE.detailValue}>{row.revises ? <span style={PAGE.mono}>{row.revises}</span> : <span style={PAGE.muted}>Not recorded</span>}</div>
              </>
            ) : null}
            <div style={PAGE.detailLabel}>Rejection reason</div>
            <div style={{ ...PAGE.detailValue, whiteSpace: "pre-wrap" }}>{row.rejectionReason || <span style={PAGE.muted}>None</span>}</div>
            <div style={PAGE.detailLabel}>URA detail<TaxHelp topic="uraDetail" /></div>
            <div style={{ ...PAGE.detailValue, whiteSpace: "pre-wrap" }}>{row.uraDetail || <span style={PAGE.muted}>None</span>}</div>
            <div style={PAGE.detailLabel}>Issued<TaxHelp topic="filedOrLodged" /></div>
            <div style={PAGE.detailValue}>{fmtDateTime(row.issuedAt) || <span style={PAGE.muted}>Not recorded</span>}</div>
            <div style={PAGE.detailLabel}>Filed</div>
            <div style={PAGE.detailValue}>{fmtDateTime(row.filedAt) || <span style={PAGE.muted}>Not filed</span>}</div>
            <div style={PAGE.detailLabel}>Lodged</div>
            <div style={PAGE.detailValue}>{fmtDateTime(row.lodgedAt) || <span style={PAGE.muted}>Not lodged</span>}</div>
          </div>
        </div>
        <div>
          {row.status === "AWAITING_APPROVAL" ? <ApprovalCheckBlock row={row} /> : null}
          {row.kind === "CREDIT_NOTE" ? (
            <CancellationBlock row={row} canManage={canManage} onRequest={onRequestCancel} />
          ) : (
            <CorrectionBlock row={row} canManage={canManage} onRaise={onRaiseCreditNote} />
          )}
        </div>
      </div>
    </td>
  </tr>
);

const FiscalDocumentsView = () => {
  const { can, loaded: permissionsLoaded } = usePermissions();
  const canManage = permissionsLoaded && can("tax:manage");

  const [filters, setFilters] = useState(filtersFromAddress);
  const [search, setSearch] = useState(() => filtersFromAddress().q);
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [environment, setEnvironment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [denied, setDenied] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [cancelRow, setCancelRow] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelReasonCode, setCancelReasonCode] = useState(DEFAULT_REASON_CODE);
  const [cancelError, setCancelError] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [fetchedAt, setFetchedAt] = useState(null);
  const [openingId, setOpeningId] = useState(null);
  const [raiseRow, setRaiseRow] = useState(null);
  const [raiseWhole, setRaiseWhole] = useState(true);
  const [raiseAmount, setRaiseAmount] = useState("");
  const [raiseReason, setRaiseReason] = useState("");
  const [raiseError, setRaiseError] = useState("");
  const [raising, setRaising] = useState(false);

  // The free-text box applies after a short pause, so a filer typing a
  // vendor's name does not fire a request per keystroke.
  useEffect(() => {
    const handle = setTimeout(() => {
      setFilters((f) => (f.q === search ? f : { ...f, q: search }));
      setPage(1);
    }, 350);
    return () => clearTimeout(handle);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await apiListFiscalDocuments(toParams(filters, page));
      setItems(Array.isArray(data?.items) ? data.items : []);
      setTotal(Number(data?.total) || 0);
      setEnvironment(data?.environment || null);
      setFetchedAt(new Date());
    } catch (err) {
      if (err?.response?.status === 403) {
        setDenied(true);
      } else {
        setError(errorText(err, "Could not load fiscal documents."));
      }
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => {
    if (!denied) load();
  }, [load, denied]);

  const setFilter = (key) => (e) => {
    const value = e.target.value;
    setFilters((f) => ({ ...f, [key]: value }));
    setPage(1);
  };

  const filtersActive = useMemo(
    () => Object.entries(filters).some(([k, v]) => k !== "q" && v) || Boolean(search),
    [filters, search],
  );

  /* Opens the printable document in the one document dialog (App.js). The
     link is signed per request and short-lived, so it is fetched on click. */
  const openDocument = async (row) => {
    setOpeningId(row.id);
    try {
      const { data } = await apiGetFiscalDocumentLink(row.id);
      if (!data?.viewUrl) throw new Error("The server did not return a document link.");
      openReceiptDialog({ viewUrl: data.viewUrl, title: data.title || KIND_LABEL[row.kind] || "Document" });
    } catch (err) {
      toast.error(errorText(err, "Could not open the document."));
    } finally {
      setOpeningId(null);
    }
  };

  const openCancel = (row) => {
    setCancelRow(row);
    setCancelReason("");
    setCancelReasonCode(DEFAULT_REASON_CODE);
    setCancelError("");
  };

  const submitCancel = async () => {
    const reason = cancelReason.trim();
    if (reason.length < MIN_REASON) {
      setCancelError("Say why the credit note should be cancelled. URA and the audit trail both see this.");
      return;
    }
    setCancelling(true);
    setCancelError("");
    try {
      const { data } = await apiCancelFiscalCreditNote(cancelRow.id, { reason, reasonCode: cancelReasonCode });
      const updated = data && data.id ? data : null;
      setItems((rows) => rows.map((r) => (r.id === cancelRow.id ? (updated || { ...r, cancellation: { status: "REQUESTED", requestedAt: new Date().toISOString(), reason, reasonCode: cancelReasonCode, outcome: null, uraDetail: null, completedAt: null } }) : r)));
      setExpanded(cancelRow.id);
      setCancelRow(null);
      toast.success("Cancellation requested. Refresh to see the answer.");
    } catch (err) {
      const code = err?.response?.data?.code;
      const message = errorText(err, "The cancellation could not be requested.");
      setCancelError(code ? `${message} (${code})` : message);
    } finally {
      setCancelling(false);
    }
  };

  const openRaise = (row) => {
    setRaiseRow(row);
    setRaiseWhole(true);
    setRaiseAmount("");
    setRaiseReason("");
    setRaiseError("");
  };

  const submitRaise = async () => {
    const reason = raiseReason.trim();
    if (reason.length < MIN_REASON) {
      setRaiseError("Say why the invoice is being credited. URA and the audit trail both see this.");
      return;
    }
    const max = Number(raiseRow?.amounts?.gross) || 0;
    const body = { invoiceId: raiseRow.id, reason };
    if (!raiseWhole) {
      const amount = Math.round(Number(raiseAmount));
      if (!Number.isFinite(amount) || amount <= 0 || amount > max) {
        setRaiseError(`The amount must be more than 0 and no more than ${fmtMoney(max, raiseRow?.amounts?.currency)}.`);
        return;
      }
      body.gross = amount;
    }
    setRaising(true);
    setRaiseError("");
    try {
      const { data } = await apiRaiseFiscalCreditNote(body);
      setRaiseRow(null);
      toast.success(data?.note || "Credit note raised. The submitter lodges it with URA.");
      await load();
      if (data?.id) setExpanded(data.id);
    } catch (err) {
      const code = err?.response?.data?.code;
      const message = errorText(err, "The credit note could not be raised.");
      setRaiseError(code ? `${message} (${code})` : message);
    } finally {
      setRaising(false);
    }
  };

  if (denied) {
    return <DeniedNotice what="Fiscal documents are URA filings made under a vendor's TIN." />;
  }


  return (
    <div style={PAGE.page}>
      <div style={PAGE.card}>
        <div style={PAGE.cardHeader}>
          <div>
            <h4 style={PAGE.cardTitle}>
              Fiscal documents
              <TaxHelp topic="fiscalDocument" />
              {environment?.mode ? <FiscalModeBadge mode={environment.mode} size="md" /> : null}
              {environment?.mode ? <TaxHelp topic="environment" /> : null}
            </h4>
            <p style={PAGE.cardSubtitle}>
              Every commission invoice and credit note the submitter has raised with URA EFRIS. A document number
              is always shown with the environment it was issued in.
            </p>
            {environment?.note ? (
              <p style={{ ...PAGE.cardSubtitle, color: "#0f172a", fontWeight: 600 }} data-testid="environment-note">{environment.note}</p>
            ) : null}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {fetchedAt ? <span style={{ ...TYPE.caption, color: "#64748b" }}>Fetched {fmtDateTime(fetchedAt)}</span> : null}
            <AppButton variant="outline" icon="fa fa-sync" onClick={load} disabled={loading}>Refresh</AppButton>
          </div>
        </div>

        <div style={PAGE.cardBody}>
          <div style={PAGE.toolbar}>
            <div className="lk-field" style={{ marginBottom: 0, minWidth: 150 }}>
              <label className="lk-field__label" htmlFor="fd-kind">Kind</label>
              <AppSelect id="fd-kind" block value={filters.kind} onChange={setFilter("kind")}>
                <option value="">All kinds</option>
                <option value="INVOICE">Invoices</option>
                <option value="CREDIT_NOTE">Credit notes</option>
              </AppSelect>
            </div>
            <div className="lk-field" style={{ marginBottom: 0, minWidth: 190 }}>
              <label className="lk-field__label" htmlFor="fd-status">Status</label>
              <AppSelect id="fd-status" block value={filters.status} onChange={setFilter("status")}>
                <option value="">Any status</option>
                {Object.entries(DOCUMENT_STATUS).map(([key, meta]) => (
                  <option key={key} value={key}>{meta.label}</option>
                ))}
              </AppSelect>
            </div>
            <div className="lk-field" style={{ marginBottom: 0, minWidth: 150 }}>
              <label className="lk-field__label" htmlFor="fd-mode">Environment</label>
              <AppSelect id="fd-mode" block value={filters.mode} onChange={setFilter("mode")}>
                <option value="">Both environments</option>
                <option value="TEST">TEST (sandbox)</option>
                <option value="LIVE">LIVE (URA production)</option>
              </AppSelect>
            </div>
            <div className="lk-field" style={{ marginBottom: 0, minWidth: 160 }}>
              <label className="lk-field__label" htmlFor="fd-vendor-type">Vendor type</label>
              <AppSelect id="fd-vendor-type" block value={filters.vendorType} onChange={setFilter("vendorType")}>
                <option value="">All vendor types</option>
                {VENDOR_TYPES.map((t) => <option key={t} value={t}>{fmtVendorType(t)}</option>)}
              </AppSelect>
            </div>
            <div className="lk-field" style={{ marginBottom: 0 }}>
              <label className="lk-field__label" htmlFor="fd-from">From</label>
              <input id="fd-from" type="date" className="lk-input" value={filters.from} onChange={setFilter("from")} />
            </div>
            <div className="lk-field" style={{ marginBottom: 0 }}>
              <label className="lk-field__label" htmlFor="fd-to">To</label>
              <input id="fd-to" type="date" className="lk-input" value={filters.to} onChange={setFilter("to")} />
            </div>
            <div className="lk-field" style={{ marginBottom: 0, flex: "1 1 220px" }}>
              <label className="lk-field__label" htmlFor="fd-q">Search</label>
              <input
                id="fd-q"
                className="lk-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Document number, FDN, buyer name or TIN"
              />
            </div>
            {filtersActive ? (
              <AppButton
                variant="quiet"
                onClick={() => { setFilters(EMPTY_FILTERS); setSearch(""); setPage(1); }}
              >
                Clear filters
              </AppButton>
            ) : null}
          </div>

          {loading ? <Loading size="panel" label="Loading fiscal documents..." /> : null}

          {!loading && error ? (
            <div className="lk-note lk-note--danger" style={{ marginBottom: 14 }}>
              <span>{error}</span>
              <AppButton size="sm" variant="quiet" onClick={load} style={{ marginLeft: "auto" }}>Retry</AppButton>
            </div>
          ) : null}

          {!loading && !error ? (
            <>
              <div className="lk-table-frame">
                <Table responsive hover className="lk-table lk-table--dense lk-fiscal-table">
                  <thead>
                    <tr>
                      <th style={{ width: 36 }}>#</th>
                      <th>Document<TaxHelp topic="documentNumber" /></th>
                      <th style={{ textAlign: "right" }}>Amount<TaxHelp topic="amount" /></th>
                      <th>Status<TaxHelp topic="status" /></th>
                      <th>URA number<TaxHelp topic="fdn" /></th>
                      <th className="lk-table-sticky-end" aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {items.length === 0 ? (
                      <tr>
                        <td colSpan={COLUMNS} style={PAGE.empty}>
                          {filtersActive
                            ? "No fiscal documents match these filters."
                            : "No fiscal documents have been raised yet. The first one appears here the moment the submitter files it."}
                        </td>
                      </tr>
                    ) : null}
                    {items.map((row, index) => {
                      const isOpen = expanded === row.id;
                      const when = row.filedAt || row.lodgedAt;
                      const whenLabel = row.filedAt ? "Filed" : row.lodgedAt ? "Lodged" : null;
                      return (
                        <React.Fragment key={row.id}>
                          <tr data-testid="fiscal-row" style={isOpen ? { background: "#f8fafc" } : undefined}>
                            <td style={{ ...PAGE.muted, fontVariantNumeric: "tabular-nums" }} data-testid="row-index">
                              {(page - 1) * PAGE_SIZE + index + 1}
                            </td>
                            <td data-testid="document-cell">
                              <span style={{ ...PAGE.strong, ...PAGE.mono }}>{row.invoiceNumber || row.id}</span>
                              <span className="lk-table-meta" style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                                <Pill tone={row.kind === "CREDIT_NOTE" ? "purple" : "info"}>{KIND_LABEL[row.kind] || row.kind}</Pill>
                                <span>
                                  {row.buyer?.name || "Unnamed buyer"}
                                  {row.buyer?.vendorType ? `, ${fmtVendorType(row.buyer.vendorType)}` : ""}
                                  {row.buyer?.tin ? `, TIN ${row.buyer.tin}` : ""}
                                </span>
                              </span>
                              {row.kind === "CREDIT_NOTE" && row.revises ? (
                                <span className="lk-table-meta">revises {row.revises}</span>
                              ) : null}
                            </td>
                            <td style={{ textAlign: "right" }}>
                              <span style={PAGE.mono}>{fmtMoney(row.amounts?.gross, row.amounts?.currency) || <span style={PAGE.muted}>No amount</span>}</span>
                              {row.amounts?.tax !== undefined && row.amounts?.tax !== null ? (
                                <span className="lk-table-meta">tax {fmtMoney(row.amounts.tax, row.amounts.currency)}</span>
                              ) : null}
                            </td>
                            <td data-testid="status-cell">
                              <StatusPill status={row.status} />
                              <span className="lk-table-meta">
                                {when ? `${whenLabel} ${fmtDateTime(when)}` : "Not yet filed"}
                              </span>
                              {row.cancellation?.status ? (
                                <span className="lk-table-meta">
                                  {(CANCELLATION_STATUS[row.cancellation.status] || { label: row.cancellation.status }).label}
                                </span>
                              ) : row.status === "AWAITING_APPROVAL" ? (
                                <span className="lk-table-meta">
                                  {row.approvalCheck?.lastCheckedAt ? `URA last asked ${fmtDateTime(row.approvalCheck.lastCheckedAt)}` : "URA not asked yet"}
                                </span>
                              ) : null}
                            </td>
                            <td data-testid="ura-cell">
                              <FiscalNumber value={row.fdn} mode={row.mode} />
                              {row.verificationCode ? (
                                <span className="lk-table-meta" style={{ overflowWrap: "anywhere" }}>
                                  verification <span style={PAGE.mono}>{row.verificationCode}</span>
                                </span>
                              ) : null}
                              {row.applicationReference ? (
                                <span className="lk-table-meta" style={{ overflowWrap: "anywhere" }} data-testid="application-ref">
                                  application ref <span style={PAGE.mono}>{row.applicationReference}</span>
                                </span>
                              ) : null}
                            </td>
                            <td className="lk-table-sticky-end" style={{ textAlign: "right" }}>
                              <span style={{ display: "inline-flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
                                {isPrintable(row) ? (
                                  <AppButton
                                    size="sm"
                                    icon="fa fa-file"
                                    onClick={() => openDocument(row)}
                                    loading={openingId === row.id}
                                    data-testid="open-document"
                                  >
                                    {DOCUMENT_BUTTON[row.kind] || "Document"}
                                  </AppButton>
                                ) : null}
                                <AppButton
                                  size="sm"
                                  variant="quiet"
                                  aria-expanded={isOpen}
                                  onClick={() => setExpanded(isOpen ? null : row.id)}
                                >
                                  {isOpen ? "Hide" : "Details"}
                                </AppButton>
                              </span>
                            </td>
                          </tr>
                          {isOpen ? (
                            <DetailRow
                              row={row}
                              canManage={canManage}
                              onRequestCancel={openCancel}
                              onRaiseCreditNote={openRaise}
                            />
                          ) : null}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </Table>
                <TablePager
                  page={page}
                  pageCount={Math.max(1, Math.ceil(total / PAGE_SIZE))}
                  totalItems={total}
                  pageSize={PAGE_SIZE}
                  itemNoun="documents"
                  onChange={setPage}
                />
              </div>
            </>
          ) : null}
        </div>
      </div>

      <AppDialog
        open={Boolean(cancelRow)}
        onClose={() => !cancelling && setCancelRow(null)}
        busy={cancelling}
        title="Ask URA to cancel this credit note"
        subtitle={cancelRow ? `Credit note ${cancelRow.invoiceNumber || cancelRow.id}` : undefined}
        footer={
          <>
            <AppButton variant="quiet" onClick={() => setCancelRow(null)} disabled={cancelling}>Keep it</AppButton>
            <AppButton variant="danger-solid" onClick={submitCancel} loading={cancelling}>Send the request</AppButton>
          </>
        }
      >
        {cancelRow ? (
          <>
            <div className="lk-note lk-note--warning" style={{ marginBottom: 14 }}>
              <span>
                The submitter asks URA to cancel this credit note by its fiscal document number. Nothing changes
                until URA confirms: the credit note stands until then, and if URA refuses, its answer is shown on
                the document in full.
              </span>
            </div>
            <div style={{ marginBottom: 12 }}>
              <FiscalNumber value={cancelRow.fdn} mode={cancelRow.mode} />
              <span style={{ ...TYPE.caption, color: "#64748b", marginLeft: 8 }}>the credit note being cancelled</span>
            </div>
            <div className="lk-field" style={{ marginBottom: 12 }}>
              <label className="lk-field__label" htmlFor="fd-cancel-reason-code">URA reason code<TaxHelp topic="cancelCreditNote" /></label>
              <AppSelect id="fd-cancel-reason-code" block value={cancelReasonCode} onChange={(e) => setCancelReasonCode(e.target.value)}>
                {CANCELLATION_REASON_CODES.map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
              </AppSelect>
            </div>
            <div className="lk-field" style={{ marginBottom: 0 }}>
              <label className="lk-field__label" htmlFor="fd-cancel-reason">Why should this credit note be cancelled?</label>
              <textarea
                id="fd-cancel-reason"
                className={["lk-input", cancelError ? "is-invalid" : ""].filter(Boolean).join(" ")}
                rows={3}
                maxLength={500}
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="e.g. Raised against the wrong invoice"
                data-autofocus
              />
              {cancelError ? <span className="lk-field__error">{cancelError}</span> : <span className="lk-field__hint">Sent to the submitter with the request and kept on the audit trail.</span>}
            </div>
          </>
        ) : null}
      </AppDialog>

      <AppDialog
        open={Boolean(raiseRow)}
        onClose={() => !raising && setRaiseRow(null)}
        busy={raising}
        title="Raise a credit note against this invoice"
        subtitle={raiseRow ? `Invoice ${raiseRow.invoiceNumber || raiseRow.id}` : undefined}
        footer={
          <>
            <AppButton variant="quiet" onClick={() => setRaiseRow(null)} disabled={raising}>Keep the invoice</AppButton>
            <AppButton onClick={submitRaise} loading={raising} data-testid="raise-submit">Raise the credit note</AppButton>
          </>
        }
      >
        {raiseRow ? (
          <>
            <div className="lk-note lk-note--warning" style={{ marginBottom: 14 }}>
              <span>
                A credit note is an application URA approves by hand. The submitter lodges it with URA and asks
                for the verdict on a schedule. Until URA approves it the credit note has an application reference
                and no fiscal document number; once approved it has its own. Crediting the whole amount marks
                this invoice void.
              </span>
            </div>
            <div style={{ marginBottom: 12 }}>
              <FiscalNumber value={raiseRow.fdn} mode={raiseRow.mode} />
              <span style={{ ...TYPE.caption, color: "#64748b", marginLeft: 8 }}>the invoice being answered</span>
            </div>
            <div className="lk-field" style={{ marginBottom: 12 }}>
              <span className="lk-field__label">Amount to credit<TaxHelp topic="creditNoteAmount" /></span>
              <AppChoice
                ariaLabel="Amount to credit"
                value={raiseWhole ? "WHOLE" : "PART"}
                onChange={(v) => setRaiseWhole(v === "WHOLE")}
                options={[
                  { value: "WHOLE", label: `Whole invoice, ${fmtMoney(raiseRow.amounts?.gross, raiseRow.amounts?.currency)}`, testId: "raise-whole" },
                  { value: "PART", label: "Part of it", testId: "raise-part" },
                ]}
              />
            </div>
            {!raiseWhole ? (
              <div className="lk-field" style={{ marginBottom: 12 }}>
                <label className="lk-field__label" htmlFor="fd-raise-amount">
                  Amount in {raiseRow.amounts?.currency || "UGX"}, gross
                </label>
                <input
                  id="fd-raise-amount"
                  className="lk-input"
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={raiseRow.amounts?.gross || undefined}
                  step={1}
                  value={raiseAmount}
                  onChange={(e) => setRaiseAmount(e.target.value)}
                  placeholder={`Up to ${Number(raiseRow.amounts?.gross || 0).toLocaleString("en-UG")}`}
                />
                <span className="lk-field__hint">The tax is worked out from this amount at the invoice's rate.</span>
              </div>
            ) : null}
            <div className="lk-field" style={{ marginBottom: 0 }}>
              <label className="lk-field__label" htmlFor="fd-raise-reason">Why is this invoice being credited?</label>
              <textarea
                id="fd-raise-reason"
                className={["lk-input", raiseError ? "is-invalid" : ""].filter(Boolean).join(" ")}
                rows={3}
                maxLength={500}
                value={raiseReason}
                onChange={(e) => setRaiseReason(e.target.value)}
                placeholder="e.g. Commission waived for the vendor's first week"
                data-autofocus
              />
              {raiseError ? <span className="lk-field__error">{raiseError}</span> : <span className="lk-field__hint">Carried to URA with the application and kept on the audit trail.</span>}
            </div>
          </>
        ) : null}
      </AppDialog>
    </div>
  );
};

export default FiscalDocumentsView;
